import { gql } from '@apollo/client';
import { ORDER_FIELDS } from '@/graphql/queries/orders';
import { ITEM_FIELDS } from '@/graphql/queries/items';
import { ROUTE_FIELDS } from '@/graphql/queries/routes';
import { RATING_FIELDS } from '@/graphql/queries/ratings';
import { CLIENT_FIELDS } from '@/graphql/queries/clients';
import { USER_FIELDS } from '@/graphql/queries/users';
import { STOP_FIELDS } from '@/graphql/queries/stops';
import { LOCATION_FIELDS } from '@/graphql/queries/locations';
import { PRICING_OVERRIDE_FIELDS } from '@/graphql/fragments/pricing_overrides';
import { LISTING_FIELDS } from '@/graphql/queries/listings';
import { BID_FIELDS } from '@/graphql/queries/bids';
import { TEAMMATE_FIELDS } from '@/graphql/queries/teammates';
import { ORDER_ROUTE_MAPPINGS } from '@/graphql/queries/order_route_mappings';
import { MANIFEST_FIELDS } from '@/graphql/queries/manifests';
import { NOTE_FIELDS } from '@/graphql/queries/notes';
import { SURVEY_QUESTION_FIELDS, SURVEY_RESPONSE_FIELDS } from '@/graphql/fragments/surveys';
import { ORDER_ROUTE_FIELDS } from '@/graphql/queries/orders';
import { DRIVER_FIELDS } from '@/graphql/fragments/drivers';
import { TAG_FIELDS } from '@/graphql/fragments/tags';
import { SERVICES_FIELDS } from '@/graphql/fragments/services';

const ENRICHED_ORDER_FIELDS = gql`
    ${ORDER_FIELDS}
    ${ITEM_FIELDS}
    ${ROUTE_FIELDS}
    ${RATING_FIELDS}
    ${CLIENT_FIELDS}
    ${USER_FIELDS}
    ${STOP_FIELDS}
    ${LOCATION_FIELDS}
    ${LISTING_FIELDS}
    ${BID_FIELDS}
    ${TEAMMATE_FIELDS}
    ${ORDER_ROUTE_MAPPINGS}
    ${MANIFEST_FIELDS}
    ${SURVEY_RESPONSE_FIELDS}
    ${SURVEY_QUESTION_FIELDS}
    ${ORDER_ROUTE_FIELDS}
    ${NOTE_FIELDS}
    ${PRICING_OVERRIDE_FIELDS}
    ${DRIVER_FIELDS}
    ${TAG_FIELDS}
    ${SERVICES_FIELDS}

    fragment EnrichedOrderFields on orders {
        ...OrderFields
        services {
            ...ServicesFields
        }
        notes {
            ...NoteFields
        }
        wh_events(order_by: [{ created_at: asc_nulls_first }, { transition: { current: asc } }, { event_id: asc }]) {
            driver {
                ...DriverFields
            }
            wh_driver {
                ...DriverFields
            }
            transition {
                next
                current
            }
        }
        survey_responses {
            ...SurveyResponseFields
            survey_question {
                ...SurveyQuestionFields
            }
        }
        itemsByOrderId {
            ...ItemFields
            manifests {
                mapping_id
                manifest_id
                manifest {
                    ...ManifestFields
                }
            }
        }
        routes {
            ...OrderRouteMappings
            route {
                ...RouteFields
                stopsByRouteId {
                    ...StopFields
                }
                teammateByDriverId {
                    ...TeammateFields
                }
            }
        }
        order_shipper {
            ...ClientFields
            user {
                ...UserFields
            }
            locations {
                ...LocationFields
            }
            tags {
                ...TagFields
            }
        }
        order_carrier {
            ...ClientFields
            user {
                ...UserFields
            }
            tags {
                ...TagFields
            }
        }
        order_middle_mile_carrier {
            ...ClientFields
            user {
                ...UserFields
            }
            tags {
                ...TagFields
            }
        }
        order_contractor {
            ...ClientFields
            user {
                ...UserFields
            }
            tags {
                ...TagFields
            }
        }
        listing {
            ...ListingFields
            bids {
                ...BidFields
            }
        }
        ratings {
            ...RatingFields
        }
        messages {
            message_id
            link_type
        }
        shipper_rate_override {
            ...PricingOverrideFields

            assign {
                client_id
                business_name
                profile_img
                profile_img_color
            }

            partner_client {
                client_id
                business_name
                profile_img
                profile_img_color
            }
        }
    }
`;

export const ORDER_BY_ID = gql`
    ${ENRICHED_ORDER_FIELDS}
    query GetOrderByID($order_id: uuid!) {
        orders(where: { order_id: { _eq: $order_id } }) {
            ...EnrichedOrderFields
        }
    }
`;

export const UPDATE_ORDER_BY_ID = gql`
    ${ENRICHED_ORDER_FIELDS}
    mutation UpdateOrderByID($order_id: uuid!, $patch: orders_set_input!) {
        update_orders(where: { order_id: { _eq: $order_id } }, _set: $patch) {
            returning {
                ...EnrichedOrderFields
            }
        }
    }
`;

export const ADD_NOTES = gql`
    ${NOTE_FIELDS}
    mutation AddNotes($notes: [notes_insert_input!]!) {
        insert_notes(objects: $notes) {
            returning {
                ...NoteFields
            }
        }
    }
`;
